import { useEffect, useRef, useState } from "react";
import { CrossIcon, Plus } from "../../../../../assets/icons";
import UseContext from "../../../../../hooks/useContext";
import ApiDatabase from "../../../../../server";
import { dateLong } from "../../../../../utils/Date";
import { durationInDecimals } from "../../../../../utils/Time";
import Button from "../../../../button";
import EmptyState from "../../../../EmptyState/EmptyState";
import StyledInput from "../../../../styledInput/StyledInput";
import SlideOverBody from "../../../component/body";
import SlideOverFooter from "../../../component/footer";
import SlideOverHeader from "../../../component/header";
import useSlideOver from "../../../useSlideOver";

const sortEntries = (entries) => {
  const filled = entries.filter((e) => e).sort();
  const empty = entries.filter((e) => !e);
  return [...filled, ...empty];
};

const getEntryLabel = (entries, index, timeCut) => {
  if (index === 0) return "Entrée";

  if (index % 2 === 0) {
    const prev = entries[index - 1];
    const current = entries[index];
    if (prev && current) {
      return durationInDecimals(prev, current) <= timeCut
        ? "Fin de pause"
        : "Fin de coupure";
    }
    return "Fin de coupure";
  }

  // Odd index: "Sortie" if last entry, otherwise pause/coupure label
  if (index === entries.length - 1) return "Sortie";

  const current = entries[index];
  const next = entries[index + 1];
  if (current && next) {
    return durationInDecimals(current, next) <= timeCut
      ? "Début de pause"
      : "Début de coupure";
  }
  return "Début de coupure";
};

const SlideOverUpdateTimeSheet = () => {
  const { slideOverData, setSlideOverDataReturn, timeCut } = UseContext();
  const { idTimeSheet, idMission, dateMission } = slideOverData;
  const { toggleSlideOver } = useSlideOver();
  const [timeEntries, setTimeEntries] = useState([]);
  const [errorEntries, setErrorEntries] = useState([]);
  const [warningEntries, setWarningEntries] = useState([]);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const initialEntriesRef = useRef([]);
  const sortTimerRef = useRef(null);
  const pendingFocusValueRef = useRef(null);
  const pendingFocusSegmentRef = useRef("hours");
  const entryRowRefs = useRef([]);

  const closeSlideOver = () => {
    toggleSlideOver();
  };

  useEffect(() => {
    if (idTimeSheet) {
      setIsWaiting(true);
      ApiDatabase.getTimeSheet(
        { id: idTimeSheet },
        (data) => {
          if (data.timeSheet?.periods.length) {
            data.timeSheet.periods.sort(
              (a, b) => a.hours.position - b.hours.position
            );

            const flat = [];
            for (const period of data.timeSheet.periods) {
              if (period.hours.start) flat.push(period.hours.start);
              if (period.hours.end) flat.push(period.hours.end);
            }

            setTimeEntries(flat);
            initialEntriesRef.current = [...flat];
          }
          setIsWaiting(false);
        },
        (err) => {
          console.log("err", err);
          setIsWaiting(false);
        }
      );
    }
  }, [idTimeSheet]);

  useEffect(() => {
    return () => {
      if (sortTimerRef.current) clearTimeout(sortTimerRef.current);
    };
  }, []);

  // Refocus the input after sort moves it to a new position, restoring hours/minutes segment
  useEffect(() => {
    if (!pendingFocusValueRef.current) return;
    const targetIndex = timeEntries.indexOf(pendingFocusValueRef.current);
    if (targetIndex >= 0 && entryRowRefs.current[targetIndex]) {
      const input = entryRowRefs.current[targetIndex].querySelector("input");
      if (input) {
        input.focus();
        if (pendingFocusSegmentRef.current === "minutes") {
          requestAnimationFrame(() => {
            try {
              input.setSelectionRange(3, 5);
            } catch {
              /* setSelectionRange not supported on time inputs in some browsers */
            }
          });
        }
      }
    }
    pendingFocusValueRef.current = null;
    pendingFocusSegmentRef.current = "hours";
  }, [timeEntries]);

  useEffect(() => {
    const errors = new Array(timeEntries.length).fill("");
    const warnings = new Array(timeEntries.length).fill("");
    const filledEntries = timeEntries.filter((e) => e);

    for (let i = 1; i < timeEntries.length; i++) {
      if (timeEntries[i] && timeEntries[i - 1]) {
        if (timeEntries[i] <= timeEntries[i - 1]) {
          errors[i] = "Les heures ne sont pas dans l'ordre";
        }
      }
    }

    if (filledEntries.length > 0 && filledEntries.length % 2 === 1) {
      const lastFilledIndex =
        timeEntries.length -
        1 -
        [...timeEntries].reverse().findIndex((e) => e);
      if (!errors[lastFilledIndex]) {
        warnings[lastFilledIndex] = "Sortie manquante";
      }
    }

    setErrorEntries(errors);
    setWarningEntries(warnings);
  }, [timeEntries]);

  const hasChanges =
    JSON.stringify(timeEntries) !== JSON.stringify(initialEntriesRef.current);

  const hasErrors = errorEntries.some((e) => e);

  const addEntry = () => {
    if (timeEntries.length < 10) {
      setTimeEntries((prev) => [...prev, ""]);
    }
  };

  const removeEntry = (index) => {
    setTimeEntries((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleEntryChange = (index, value) => {
    const oldValue = timeEntries[index];

    // Detect edited segment by comparing which part of the time string changed
    let segment = "hours";
    if (oldValue && value && oldValue.length >= 5 && value.length >= 5) {
      const hoursChanged = oldValue.substring(0, 2) !== value.substring(0, 2);
      const minutesChanged = oldValue.substring(3, 5) !== value.substring(3, 5);
      if (minutesChanged && !hoursChanged) segment = "minutes";
    }

    setTimeEntries((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });

    if (sortTimerRef.current) clearTimeout(sortTimerRef.current);
    if (value) {
      sortTimerRef.current = setTimeout(() => {
        pendingFocusValueRef.current = value;
        pendingFocusSegmentRef.current = segment;
        setTimeEntries((prev) => sortEntries(prev));
      }, 1500);
    }
  };

  const handleEntryBlur = () => {
    if (sortTimerRef.current) clearTimeout(sortTimerRef.current);
    pendingFocusValueRef.current = null;
    setTimeEntries((prev) => sortEntries(prev));
  };

  const handleSubmit = () => {
    setIsLoading(true);

    const filledEntries = timeEntries.filter((e) => e);
    const timeSheet = [];
    for (let i = 0; i < filledEntries.length; i += 2) {
      timeSheet.push({
        start: filledEntries[i],
        end: filledEntries[i + 1] || "",
      });
    }

    if (!idTimeSheet) {
      if (timeSheet.length === 0 || hasErrors) {
        setIsLoading(false);
        return;
      }

      ApiDatabase.postCreateTimeSheetMission(
        { idMission, date: dateMission, timeSheet },
        (data) => {
          setIsLoading(false);
          setSlideOverDataReturn({
            idMission,
            date: dateMission,
            idTimeSheet: data.idTimeSheet,
            timeSheet: data.timeSheet,
            pause: data.pause,
            totalHours: data.totalHours,
            "totalHoursNight5%": data["totalHoursNight5%"],
            "totalHoursNight20%": data["totalHoursNight20%"],
          });
          closeSlideOver();
        },
        (err) => {
          setIsLoading(false);
          console.log(err);
        }
      );
    } else {
      ApiDatabase.postUpdateTimeSheet(
        { idTimeSheet, timeSheet },
        (data) => {
          setIsLoading(false);
          setSlideOverDataReturn({
            idMission,
            date: dateMission,
            timeSheet: data.timeSheet,
            pause: data.pause,
            totalHours: data.totalHours,
            "totalHoursNight5%": data["totalHoursNight5%"],
            "totalHoursNight20%": data["totalHoursNight20%"],
          });
          closeSlideOver();
        },
        (err) => {
          setIsLoading(false);
          console.log(err);
        }
      );
    }
  };

  return (
    <>
      <SlideOverHeader
        className={"slide-over-update-time-sheet-header"}
        hide={closeSlideOver}
        hideShow={true}
      >
        <div className={"capitalize"}>{dateLong(new Date(dateMission))}</div>
        <div className={"text-sm text-gray-500"}>
          Modifiez les heures pointées par les profils.
        </div>
      </SlideOverHeader>
      <SlideOverBody className={"slide-over-update-time-sheet-body"}>
        <div className={"flex flex-col gap-4 h-full"}>
          {isWaiting ? (
            <>
              <div
                className={
                  "flex justify-between items-center gap-2.5 animate-pulse"
                }
              >
                <div className={"h-4 bg-gray-200 rounded w-20"}></div>
                <div className={"h-6 bg-gray-200 rounded w-60"}></div>
              </div>
              <div className={"h-0.5 bg-gray-50 w-full"}></div>
              <div
                className={
                  "flex justify-between items-center gap-2.5 animate-pulse"
                }
              >
                <div className={"h-4 bg-gray-200 rounded w-20"}></div>
                <div className={"h-6 bg-gray-200 rounded w-60"}></div>
              </div>
            </>
          ) : (
            <>
              {timeEntries.length === 0 ? (
                <div
                  className={
                    "flex flex-col justify-center items-center h-full"
                  }
                >
                  <EmptyState
                    type={"mission_time_sheet_period"}
                    custom={
                      <div className={"w-fit my-6"}>
                        <Button
                          size={"SM"}
                          color={"SECONDARY"}
                          name={
                            "slide-over-update-time-sheet-body-add-periods"
                          }
                          onClick={addEntry}
                        >
                          <div className={"flex gap-1 items-center"}>
                            <div>
                              <Plus wh={20} color={"#374151"} />{" "}
                            </div>
                            <div>Ajouter un pointage</div>
                          </div>
                        </Button>
                      </div>
                    }
                  />
                </div>
              ) : (
                <>
                  {timeEntries.map((entry, index) => (
                    <div
                      key={index}
                      ref={(el) => (entryRowRefs.current[index] = el)}
                    >
                      {index > 0 && (
                        <div className={"h-0.5 bg-gray-50 w-full mb-3"} />
                      )}
                      <div
                        className={"flex items-center gap-2.5"}
                      >
                        <div className={"w-36 shrink-0 text-sm"}>
                          {getEntryLabel(timeEntries, index, timeCut)}
                        </div>
                        <div
                          className={"flex-1"}
                          onBlur={handleEntryBlur}
                        >
                          <StyledInput
                            type={"time"}
                            value={entry}
                            onChange={(e) =>
                              handleEntryChange(index, e.target.value)
                            }
                            error={errorEntries[index] || warningEntries[index]}
                          />
                        </div>
                        <Button
                          size={"ICON"}
                          color={"ICON"}
                          name={
                            "slide-over-update-time-sheet-body-delete-entry"
                          }
                          onClick={() => removeEntry(index)}
                        >
                          <CrossIcon wh={20} color={"#374151"} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <div className={"w-fit"}>
                    <Button
                      size={"SM"}
                      color={"SECONDARY"}
                      name={"slide-over-update-time-sheet-body-add-entry"}
                      onClick={addEntry}
                    >
                      <div className={"flex gap-1 items-center"}>
                        <div>
                          <Plus wh={20} color={"#374151"} />{" "}
                        </div>
                        <div>Ajouter</div>
                      </div>
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </SlideOverBody>
      <SlideOverFooter className={"slide-over-update-time-sheet-footer"}>
        <Button size={"SM"} color={"SECONDARY"} onClick={closeSlideOver}>
          Fermer
        </Button>
        {isLoading ? (
          <Button size={"SM"} color={"DISABLED"}>
            Confirmation...
          </Button>
        ) : hasErrors || !hasChanges ? (
          <Button size={"SM"} color={"DISABLED"}>
            Confirmer
          </Button>
        ) : (
          <Button
            size={"SM"}
            color={"PRIMARY"}
            name={"slide-over-update-time-sheet-footer-confirm"}
            onClick={handleSubmit}
          >
            Confirmer
          </Button>
        )}
      </SlideOverFooter>
    </>
  );
};

export default SlideOverUpdateTimeSheet;
